import Custodian from '@advisor/models/store/custodian/index';
// eslint-disable-next-line import/no-cycle
import RwCustodianService from '@advisor/rwSdk/CustodianService';
import RwAdvisorService from '@advisor/rwSdk/AdvisorService';
import {CUSTODIAN} from '@advisor/util/constants';
import PII from '@advisor/util/PII';
import exceptionHandler from '@advisor/util/exceptionHandler';

export const custodianState = function () {
  return new Custodian();
};

const actions = {
  /**
   * This will return all us states
   * @param {} param0
   * @param {} payload
   */
  async getUsStates({commit}) {
    try {
      const sdkResponse = await RwCustodianService.getUsStates({}).catch((error) => {
        throw error;
      });

      commit('SET_US_STATES', sdkResponse);

      return Promise.resolve(sdkResponse);
    } catch (error) {
      exceptionHandler.handleGenericException('custodian.getUsStates', error);

      return Promise.reject(error);
    }
  },

  /**
   * This returns shared rep codes
   * @param {} param0
   * @param {riaId} payload `
   */
  async getSharedRepCodes(context, payload) {
    try {
      const options = {repCodeStatus: 1, ...payload};

      const response = await RwCustodianService.getSharedRepCodes(options).catch((error) => {
        throw error;
      });

      return Promise.resolve(response);
    } catch (error) {
      exceptionHandler.handleGenericException('custodian.getSharedRepCodes', error);

      return Promise.reject(error);
    }
  },

  /**
   *
   * This returns the list of custodian types
   * @param {} param0
   * @param {} payload
   *
   * @returns {Array<CustodianType>}
   */
  async getCustodianTypes({commit}, payload) {
    const riaId = await this.dispatch('getRiaId');

    try {
      const custodians = await RwCustodianService.getCustodianInfo({
        riaId,
        ...payload,
      }).catch((error) => {
        throw error;
      });

      commit('SET_CUSTODIAN_TYPES', custodians);

      return custodians;
    } catch (error) {
      exceptionHandler.handleGenericException('custodian.getCustodianInfo', error);

      return Promise.reject(error);
    }
  },

  /**
   * This will return employment statuses
   * @param {} param0
   * @param {} payload
   */
  async getEmploymentStatuses(context, payload) {
    try {
      const response = await RwCustodianService.getClientProfessionTypes(payload).catch((error) => {
        throw error;
      });

      return Promise.resolve(response);
    } catch (error) {
      exceptionHandler.handleGenericException('custodian.getEmploymentStatuses', error);

      return Promise.reject(error);
    }
  },

  async getRelationships({commit}, payload = {}) {
    try {
      const sdkResponse = await RwCustodianService.getClientRelationships(payload).catch((error) => {
        throw error;
      });

      commit('SET_RELATIONSHIPS', sdkResponse);

      return Promise.resolve(sdkResponse);
    } catch (error) {
      exceptionHandler.handleGenericException('custodian.getRelationships', error);

      return Promise.reject(error);
    }
  },

  async getCitizenships() {
    try {
      const response = await RwCustodianService.getCitizenships({}).catch((error) => {
        throw error;
      });

      return Promise.resolve(response);
    } catch (error) {
      exceptionHandler.handleGenericException('custodian.getCitizenships', error);

      return Promise.reject(error);
    }
  },

  // eslint-disable-next-line complexity, max-lines-per-function
  async fetchExistingAccountDetails({rootState}, payload) {
    try {
      const {clientOnBoarding} = rootState.client;

      // Pre-populate personal info
      clientOnBoarding.personalInfo.repCode = payload.repCode;
      clientOnBoarding.personalInfo.custodian = CUSTODIAN[payload.custodian] ? CUSTODIAN[payload.custodian] : null;

      // Account type
      clientOnBoarding.selectedAccount.accountType = payload.accountType;

      // Dividend Settings
      clientOnBoarding.dividendSetting = payload.dividendSetting;

      // Pre-populate goal info
      if (payload.goalId) {
        const objClientGoal = await this.dispatch('client/getClientGoal', {
          goalId: payload.goalId,
        });

        const {segmentGoalId, goalName, displayType, retirementAge, targetDate} = objClientGoal;

        clientOnBoarding.selectedGoal = {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          segment_goal_Id: segmentGoalId,
          goal_name: goalName,
          displayType,
          retirementAge,
          targetDate,
        };
      }

      // Pre-populate employment info
      clientOnBoarding.tradedCompanyInfo = payload.disclosures.publicCompanyHolder
        ? payload.disclosures.publicCompanyHolder
        : clientOnBoarding.tradedCompanyInfo;

      clientOnBoarding.firmAssociationInfo = payload.disclosures.exchangeEntity
        ? payload.disclosures.exchangeEntity
        : clientOnBoarding.firmAssociationInfo;

      clientOnBoarding.publicOfficialInfo = payload.disclosures.politicalExposureDetail
        ? payload.disclosures.politicalExposureDetail
        : clientOnBoarding.publicOfficialInfo;

      clientOnBoarding.employeeInfo = payload.employment
        ? {...clientOnBoarding.employeeInfo, ...payload.employment}
        : clientOnBoarding.employeeInfo;

      clientOnBoarding.employementSettings.tradedCompanyToggle = payload.disclosures.publicCompanyHolder !== null;
      clientOnBoarding.employementSettings.firmAssociationToggle = payload.disclosures.exchangeEntity !== null;
      clientOnBoarding.employementSettings.publicOfficialToggle = payload.disclosures.politicalExposureDetail !== null;

      clientOnBoarding.investmentProfile = payload.investmentProfile
        ? payload.investmentProfile
        : clientOnBoarding.investmentProfile;

      // Pre-populate Beneficiary details
      clientOnBoarding.designatedBeneficiaries =
        payload.beneficiaries && payload.beneficiaries.length > 0 ? PII.clean(payload.beneficiaries) : [];
      clientOnBoarding.designateBeneficiary.addBeneficiaries = payload.beneficiaries && payload.beneficiaries.length > 0;

      // Pre-populated trusted contact details
      if (payload.trustedContact) {
        clientOnBoarding.trustedContact = payload.trustedContact;
        clientOnBoarding.designateBeneficiary.addTrustedContact = true;
      }

      await this.dispatch('client/setClientOnBoardingData', clientOnBoarding, {
        root: true,
      });
    } catch (error) {
      exceptionHandler.handleGenericException('custodian.setClientOnBoardingData', error);
    }
  },

  /**
   *  This fetches dividend setting from clients segment.
   * @param {Object}
   * @param {Number} clUserId
   */
  async fetchDefaultDividendSetting({rootState, dispatch}, clUserId) {
    try {
      const {riaId, client} = rootState;
      const clientDetails = await dispatch(
        'client/getClientWithSubResources',
        {
          id: clUserId,
          subResources: 'segment',
        },
        {root: true}
      );
      const segmentId = clientDetails?.segment?.id;
      const objResponse = await RwAdvisorService.getOnboardingData({
        riaId,
        segmentId,
      });

      const clientSegmentOnboardingDetails = objResponse;
      const {clientOnBoarding} = client;

      clientOnBoarding.dividendSetting = clientSegmentOnboardingDetails?.dividendSetting;
      await dispatch('client/setClientOnBoardingData', clientOnBoarding, {
        root: true,
      });
    } catch (error) {
      exceptionHandler.handleGenericException('custodian.fetchDefaultDividendSetting', error);
    }
  },

  async getAccountRequestDetails(context, payload) {
    try {
      const accountRequestId = payload.requestId;
      const objResponse = await RwCustodianService.getAccountRequest({
        accountRequestId,
      }).catch((error) => {
        throw error;
      });

      return Promise.resolve(objResponse);
    } catch (error) {
      exceptionHandler.handleGenericException('custodian.getAccountRequestDetails', error);

      return Promise.reject(error);
    }
  },

  async getNetWorthIntervals() {
    try {
      const response = await RwCustodianService.getClientNetWorths({}).catch((error) => {
        throw error;
      });

      return Promise.resolve(response);
    } catch (error) {
      exceptionHandler.handleGenericException('custodian.getNetWorthIntervals', error);

      return Promise.reject(error);
    }
  },

  async getIncomeIntervals() {
    try {
      const response = await RwCustodianService.getClientIncomeIntervals({}).catch((error) => {
        throw error;
      });

      return Promise.resolve(response);
    } catch (error) {
      exceptionHandler.handleGenericException('custodian.getIncomeIntervals', error);

      return Promise.reject(error);
    }
  },

  /**
   * This will fetch and return the clients beneficiaries
   * @param {commit} param0 to commit the values to store
   * @param {Number} payload client Id
   */
  async getBeneficiaries(context, payload) {
    try {
      const {clUserId} = payload;
      const accountRequestId = payload.accountRequestId ? payload.accountRequestId : null;
      const include = PII.toUrlString([PII.getProps().SSN]);

      const response = await RwCustodianService.getBeneficiaries({
        clUserId,
        accountRequestId,
        include,
      }).catch((error) => {
        throw error;
      });

      return Promise.resolve(response);
    } catch (error) {
      exceptionHandler.handleGenericException('custodian.getBeneficiaries', error);

      return Promise.reject(error);
    }
  },

  /**
   * @typedef {Object} beneficiaryRequest
   * @property {Number} beneficiaryId
   * @property {Number} clUserId
   * @property {Number} accountRequestId
   * @property {Array} piiProps
   *
   * @param {Object} context
   * @param {beneficiaryRequest} params
   *
   * @returns {Beneficiary}
   */
  async getBeneficiary(context, payload) {
    try {
      const {beneficiaryId, clUserId, accountRequestId, piiProps} = payload;

      const fullPayload = {beneficiaryId, clUserId};

      if (piiProps) {
        fullPayload.include = piiProps;
      }

      if (accountRequestId) {
        fullPayload.accountRequestId = accountRequestId;
      }

      const response = await RwCustodianService.getBeneficiary(fullPayload).catch((error) => {
        throw error;
      });

      return Promise.resolve(response);
    } catch (error) {
      exceptionHandler.handleGenericException('custodian.getBeneficiary', error);

      return Promise.reject(error);
    }
  },

  /**
   * This will fetch existing trusted contact
   * @param {commit} param0 to commit to store
   * @param {clUserId} payload client Id
   */
  async getClientTrustedContact(context, payload) {
    try {
      const response = await RwCustodianService.getTrustedContact(payload).catch((error) => {
        throw error;
      });

      return Promise.resolve(response);
    } catch (error) {
      exceptionHandler.handleGenericException('custodian.getClientTrustedContact', error);

      return Promise.reject(error);
    }
  },

  /**
   * Action to get account additional info headers, which includes cash sweep vehicles, etc.
   */
  async getAccountAdditionalHeadersInfo(payload = {}) {
    try {
      const response = await RwCustodianService.getAdditionalInfoHeader(payload).catch((error) => {
        throw error;
      });

      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  /**
   * This will delete the beneficiaries of given account request Id
   * @param {Number} accountRequestId
   */
  async deleteBeneficiaries(context, accountRequestId) {
    try {
      const response = await RwCustodianService.deleteBeneficiaries({
        accountRequestId,
      }).catch((error) => {
        throw error;
      });

      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  /**
   * This will delete the trusted contact of given account request Id
   * @param {requestId} payload Account request Id
   */
  async deleteTrustedContact(context, payload) {
    try {
      const response = await RwCustodianService.removeTrustedContact(payload).catch((error) => {
        throw error;
      });

      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  getRepCodesByCustodianId({state}, custodianId) {
    return state.custodianTypes[custodianId] ? state.custodianTypes[custodianId].custodianRepCode : [];
  },

  async custodianPerformanceDateMap({commit}, payload = {}) {
    try {
      const data = await RwCustodianService.getLatestCustodianLoadDate(payload).catch((error) => {
        throw error;
      });
      // Action responsible for generating custodianId: date -> map
      const makeCustodianPerformanceDateMap = (mapArr) =>
        mapArr?.reduce(
          (acc, {custodianId, date}) => ({
            ...acc,
            [custodianId]: date,
          }),
          {}
        );

      const dataMap = makeCustodianPerformanceDateMap(data);

      commit('SET_CUSTODIAN_PERFORMANCE_DATE_MAP', dataMap);

      return Promise.resolve(data);
    } catch (error) {
      return Promise.reject(error);
    }
  },
};

const mutations = {
  SET_CUSTODIAN_TYPES(state, payload) {
    state.orderedCustodianTypes = [...payload];

    // Formatting for easy accessibility
    const custodianTypes = {};

    payload.forEach((item) => {
      custodianTypes[item.custodianId] = item;
    });
    state.custodianTypes = custodianTypes;
  },

  SET_US_STATES(state, payload) {
    state.states = payload;
  },

  SET_RELATIONSHIPS(state, payload) {
    state.relationships = payload;
  },

  SET_CUSTODIAN_PERFORMANCE_DATE_MAP(state, payload) {
    state.custodianPerformanceDateMap = payload;
  },
};

export default {
  namespaced: true,
  state: custodianState,
  actions,
  mutations,
};
